import { GridItem } from '@/components'
import { ID } from '@/types/model'
import {
  HomeScheduleCard,
  Thermostat,
  YourLastAdjustmentCard,
} from '../../../../../features/smart-thermostats'
import { ThermostatProvider } from '@/types/thermostat'
import ErrorBoundary, {
  LocationTag,
} from '@/authenticated/components/ErrorBoundary'
import { LockedFeature } from '@/app/components'
import { remoteConfig } from '@/firebase'
import Tabs from '@/components/Tabs'
import ThermostatCostSavingsCard from './components/ThermostatCostSavings/ThermostatCostSavingsCard'
import ThermostatCostsBarGraph from './components/ThermostatCostSavings/ThermostatBarGraphs/ThermostatCostsBarGraph'
import ThermostatSavingsBarGraph from './components/ThermostatCostSavings/ThermostatBarGraphs/ThermostatSavingsBarGraph'
import ThermostatSavingsCalculationModal from './components/ThermostatCostSavings/ThermostatSavingsCalculationModal'
import ThermostatSavingsChip from './components/ThermostatCostSavings/ThermostatSavingsChip'
import useThermostatCostSavingsViewModel from './components/ThermostatCostSavings/useThermostatCostSavingsViewModel'
import { useEffect } from 'react'
import { setActiveTabIndex } from '@/components/Tabs/Tabs'

export interface Props {
  thermostatId: ID
  thermostatName: ThermostatProvider
}

export default function ThermostatSection(props: Props) {
  const isEcobee = props.thermostatName === 'Ecobee'

  const hide_nest_schedule =
    (remoteConfig?.getBoolean('hide_nest_schedule_components') ?? true) &&
    props.thermostatName === 'Nest'

  const savingsViewModel = useThermostatCostSavingsViewModel({
    thermostatId: props.thermostatId,
    thermostatName: props.thermostatName,
  })

  // Check if there are no savings in the month period
  const hasNoMonthSavings =
    !savingsViewModel.loading &&
    (savingsViewModel.monthCostOfOptiwattAdjustmentsCents >= 0 ||
      savingsViewModel.monthSavingsAmount === 0)

  // Default to Savings (index 0)
  const defaultTabIndex = 0

  // Switch to Costs tab (index 1) when "No Savings Currently" appears
  useEffect(() => {
    if (hasNoMonthSavings) {
      setActiveTabIndex('devices-energy-system-tabs', 1)
    }
  }, [hasNoMonthSavings])

  return (
    <>
      {!isEcobee && (
        <GridItem placeSelf="center" style={{ width: '100%' }}>
          <ErrorBoundary
            location={LocationTag.Thermostat}
            functionalityDescription="Thermostat"
          >
            <Thermostat id={props.thermostatId} />
          </ErrorBoundary>
        </GridItem>
      )}
      <ErrorBoundary
        location={LocationTag.YourLastAdjustmentCard}
        functionalityDescription="Last Adjustment Summary"
        fallbackOnCard
      >
        <YourLastAdjustmentCard thermostatId={props.thermostatId} />
      </ErrorBoundary>

      <Tabs.Tabs
        initialTabIndex={defaultTabIndex}
        tabKey="devices-energy-system-tabs"
        labels={['Savings', 'Costs']}
        tabBarAttributes={{ className: 'max-w-none' }}
        onTabChange={() => {}}
      />
      <Tabs.Content tabKey="devices-energy-system-tabs">
        <ErrorBoundary
          location={LocationTag.ThermostatSavingsCard}
          functionalityDescription="Total Savings Summary"
          fallbackOnCard
        >
          <LockedFeature
            title="Total Savings"
            connectionRequirements={['utility', 'unpendingPlan']}
          >
            <ThermostatCostSavingsCard
              thermostatId={props.thermostatId}
              thermostatName={props.thermostatName}
              title="Total Savings"
              showAvgSetPoint
              showAvgOutsideTemp
              barGraphComponent={ThermostatSavingsBarGraph}
              infoModalComponent={ThermostatSavingsCalculationModal}
              savingsChipComponent={ThermostatSavingsChip}
            />
          </LockedFeature>
        </ErrorBoundary>
        <ErrorBoundary
          location={LocationTag.ThermostatCostsCard}
          functionalityDescription="Total Spent Summary"
          fallbackOnCard
        >
          <LockedFeature
            title="Total Spent"
            connectionRequirements={['utility', 'unpendingPlan']}
          >
            <ThermostatCostSavingsCard
              thermostatId={props.thermostatId}
              thermostatName={props.thermostatName}
              title="Total Spent"
              barGraphComponent={ThermostatCostsBarGraph}
              showCostOfSchedule={!hide_nest_schedule}
              showCostOfAdjustments
              showAvgSetPoint
              showAvgOutsideTemp
              showStaleDataIndicator
            />
          </LockedFeature>
        </ErrorBoundary>
      </Tabs.Content>
      <ErrorBoundary
        location={LocationTag.HomeSchedulCard}
        functionalityDescription="Home Schedule Summary"
        fallbackOnCard
      >
        {!hide_nest_schedule && (
          <LockedFeature
            title="Estimated Monthly Costs"
            connectionRequirements={['utility', 'unpendingPlan']}
          >
            <HomeScheduleCard thermostatId={props.thermostatId} />
          </LockedFeature>
        )}
      </ErrorBoundary>
    </>
  )
}
